import "@medplum/react/styles.css";
import "@mantine/core/styles.css";
import "@mantine/notifications/styles.css";
import "@mantine/dates/styles.css";
import "@mantine/spotlight/styles.css";

import { StrictMode } from "react";
import { createBrowserRouter } from "react-router-dom";

import { MedplumClient, OperationOutcomeError } from "@medplum/core";
import { OperationOutcome } from "@medplum/fhirtypes";
import { MedplumProvider } from "@medplum/react";

import { MantineProvider } from "@mantine/core";
import { ModalsProvider } from "@mantine/modals";
import { Notifications, notifications } from "@mantine/notifications";

import * as Sentry from "@sentry/react";
import { ErrorBoundary } from "@sentry/react";
import { createRoot } from "react-dom/client";

import { App } from "@/App";
import routes from "@/routes";

import { AppRoutes } from "./AppRoutes";
import { GRACE_PERIOD } from "./constants/medplum";
import { mantineTheme } from "./theme/mantineTheme";
import { setNextRedirectUrl } from "./utils/medplum";

// Vite-specific preload error handling (recommended by Vite)
// https://vite.dev/guide/build.html#load-error-handling
window.addEventListener("vite:preloadError", (event) => {
  event.preventDefault();
  window.location.reload();
});

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
  environment: import.meta.env.VITE_ENV,
  tunnel: import.meta.env.VITE_SENTRY_TUNNEL,
  tracesSampleRate: 1.0,
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: [
    "localhost",
    /^https:\/\/api\.medplum\.com/,
    /^https:\/\/ehr-git-develop-quiltedhealth\.vercel\.app/,
    /^https:\/\/ehr-git-main-quiltedhealth\.vercel\.app/,
    /^https:\/\/ehr-git-demo-quiltedhealth\.vercel\.app/,
    /^https:\/\/ehr\.quiltedhealth\.com/,
    /^https:\/\/staging\.quiltedhealth\.com/,
    /^https:\/\/demo\.quiltedhealth\.com/,
  ],
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  beforeSend: (event, hint) => {
    const error = hint.originalException;
    if (!(error instanceof OperationOutcomeError)) {
      return event;
    }

    const outcome: OperationOutcome = error.outcome;
    /**
     * Checks if an error is a Medplum OperationOutcomeError with 401 or 403 status.
     * Based on Medplum SDK's outcomes.ts:
     * - 401 Unauthorized: outcome.id === 'unauthorized'
     * - 403 Forbidden: outcome.id === 'forbidden'
     */
    if (["unauthorized", "forbidden"].includes(outcome.id ?? "")) return null;
    return event;
  },
});

export async function initApp(): Promise<void> {
  const baseUrl = import.meta.env.VITE_MEDPLUM_URL;
  const router = createBrowserRouter([
    {
      element: <App />,
      children: routes,
    },
  ]);

  const navigate = (path: string): Promise<void> => router.navigate(path);

  const medplum = new MedplumClient({
    refreshGracePeriod: GRACE_PERIOD,
    clientId: import.meta.env.VITE_MEDPLUM_LOGIN_TOKEN_ID,
    cacheTime: 60000,
    autoBatchTime: 100,
    onUnauthenticated: async () => {
      const currentPath = window.location.pathname;
      if (currentPath !== "/" && currentPath !== "/oauth") {
        notifications.clean();
        notifications.show({
          title: "Session Expired",
          message: "Your session has expired. Please sign in again.",
          color: "yellow",
          autoClose: 5000,
        });

        // Sign out and redirect
        medplum.signOut();

        // Use window.location.href to ensure a full page reload and proper redirection
        setNextRedirectUrl();
      }
    },
    ...(baseUrl ? { baseUrl } : {}),
  });

  const root = createRoot(document.getElementById("root") as HTMLElement);
  root.render(
    <ErrorBoundary>
      <StrictMode>
        <MedplumProvider medplum={medplum} navigate={navigate}>
          <MantineProvider theme={mantineTheme}>
            <ModalsProvider>
              <Notifications zIndex={10000} position="bottom-right" />
              <AppRoutes />
            </ModalsProvider>
          </MantineProvider>
        </MedplumProvider>
      </StrictMode>
    </ErrorBoundary>,
  );
}

if (process.env.NODE_ENV !== "test") {
  initApp().catch(console.error);
}
